import React, { useState, useMemo, useEffect, useRef } from "react";
import { sendOTP, verifyOTP } from "../../../services/OTPService";
import { getPatientDetails } from "../../../services/PatientsService";
import axiosInstance from "../../../services/AxiosInstance";
import logo from "../../../assets/images/logo.png";
import { handleSuccess, showErrorAlert } from "../../../services/ErrorHandlingService";
import { useNavigate } from "react-router-dom";

const OTPVerificationPage = ({ patientId: encodedPatientId, formId: encodedFormId }) => {
  // Decode IDs
  const navigate = useNavigate();
  const patientId = useMemo(() => {
    try {
      return atob(encodedPatientId);
    } catch {
      return null;
    }
  }, [encodedPatientId]);

  const formId = useMemo(() => {
    try {
      return atob(encodedFormId);
    } catch {
      return null;
    }
  }, [encodedFormId]);

  const [patientWhatsapp, setPatientWhatsapp] = useState("");
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [otpValues, setOtpValues] = useState(Array(6).fill(""));
  const [otpLength] = useState(6);
  const otpRefs = useRef([]);
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);


  // Fetch patient details
  useEffect(() => {
    const fetchPatientWhatsapp = async () => {
      try {
        const res = await getPatientDetails(patientId);
        if (res?.data?.status === "success" && res.data.data) {
          setPatientWhatsapp(res.data.data.whatsapp_contact_no);
        } else {
          showErrorAlert("Unable to fetch patient details.");
        }
      } catch {
        showErrorAlert("Error fetching patient details.");
      }
    };

    // if (patientId) fetchPatientWhatsapp();
  }, [patientId]);

  // Resend countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [resendTimer]);

  // Send OTP
  const handleSendOTP = async () => {
    if (!whatsappNumber || whatsappNumber.length !== 10) {
      return showErrorAlert("Please enter a valid 10-digit WhatsApp number.");
    }
    if (!termsAccepted) {
      return showErrorAlert("Please accept the Terms and Conditions.");
    }
    // if (whatsappNumber !== patientWhatsapp) {
    //   return showErrorAlert("Entered number does not match registered WhatsApp number.");
    // }

    try {
      setSending(true);
      const response = await sendOTP({
        patient_id: patientId,
        form_id: formId,
        mobile_no: whatsappNumber
      });
      if (response.status !== "success") throw new Error(response.message || "Failed to send OTP");

      setOtpSent(true);
      setResendTimer(60);
      setOtpValues(Array(otpLength).fill(""));
      handleSuccess("OTP has been sent successfully!");
    } catch (err) {
      showErrorAlert(err.message || "Error sending OTP.");
    } finally {
      setSending(false);
    }
  };

  // OTP change
  const handleOtpChange = (value, index) => {
    if (!/^[0-9]?$/.test(value)) return;
    const newOtp = [...otpValues];
    newOtp[index] = value;
    setOtpValues(newOtp);
    if (value && index < otpLength - 1) {
      otpRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otpValues[index] && index > 0) {
      otpRefs.current[index - 1].focus();
    }
  };

  // Verify OTP
  const handleVerifyOTP = async () => {
    const otp = otpValues.join("");
    if (!otp || otp.length !== otpLength) {
      return showErrorAlert(`Please enter a ${otpLength}-digit OTP.`);
    }

    try {
      setVerifying(true);
      const verifyResponse = await verifyOTP({
        patient_id: patientId,
        form_id: formId,
        mobile_no: whatsappNumber,
        otp
      });

      if (verifyResponse.status !== "success") {
        throw new Error("Invalid or expired OTP. Please try again.");
      }

      handleSuccess("OTP verified successfully!");
      setLoading(true);
      // navigate(`/forms/patient-form/${encodedFormId}/${encodedPatientId}`, { replace: true });
      window.location.reload(`/forms/patient-form/${encodedFormId}/${encodedPatientId}`);

      // setTimeout(() => {
      //   window.location.reload();
      // }, 200);

      // const formDetailsResponse = await axiosInstance.post("/admin/formDetails", {
      //   form_id: formId,
      //   patient_id: patientId
      // });

      // if (formDetailsResponse?.data?.data?.is_verify === "Yes") {
      //   // window.location.href = `https://rohati-dev.dreamcastsolutions.com/forms/patient-form/${encodedFormId}/${encodedPatientId}`;
      //   navigate(`/forms/patient-form/${encodedFormId}/${encodedPatientId}`);

      // } else {
      //   showErrorAlert("Consent not completed yet. Please try again.");
      // }
    } catch (err) {
      showErrorAlert(err.message || "Verification failed.");
    } finally {
      setVerifying(false);
    }
  };


  return (

    <div className="container mt-5" style={{ maxWidth: "500px" }}>
      {loading && (
        <div className="loader-overlay">
          <div className="spinner"></div>
          <p>Loading, please wait...</p>
        </div>
      )}
      <div className="text-center mb-4">
        <img src={logo} alt="Logo" style={{ width: "200px", height: "auto" }} />
      </div>

      <h4 className="text-center">Consent & WhatsApp OTP Verification</h4>
      <p className="text-center">Enter your registered WhatsApp number to receive an OTP.</p>

      <input
        className="form-control mb-3"
        type="text"
        placeholder="Enter WhatsApp Number"
        value={whatsappNumber}
        onChange={(e) => setWhatsappNumber(e.target.value)}
        maxLength={10}
      />

      <div
        className="d-flex align-items-center mb-3"
        style={{ gap: "10px", fontSize: "15px" }}
      >
        <input
          className="form-check-input"
          type="checkbox"
          id="termsCheckbox"
          checked={termsAccepted}
          onChange={(e) => setTermsAccepted(e.target.checked)}
          style={{ width: "18px", height: "18px", cursor: "pointer" }}
        />
        <label
          className="form-check-label mb-0"
          htmlFor="termsCheckbox"
          style={{ cursor: "pointer", lineHeight: "1.4" }}
        >
          I agree to the{" "}
          <a href="/terms" target="_blank" rel="noreferrer">
            Terms and Conditions
          </a>
        </label>
      </div>

      <button
        className="btn btn-primary mb-3 w-100"
        onClick={handleSendOTP}
        disabled={sending || resendTimer > 0}
      >
        {sending
          ? "Sending OTP..."
          : resendTimer > 0
            ? `Resend OTP in ${resendTimer}s`
            : otpSent
              ? "Resend OTP"
              : "Send OTP"}
      </button>

      {otpSent && (
        <>
          <div style={{ display: "flex", gap: "10px", justifyContent: "center", marginBottom: "15px" }}>
            {otpValues.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (otpRefs.current[index] = el)}
                type="text"
                maxLength="1"
                value={digit}
                onChange={(e) => handleOtpChange(e.target.value, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                style={{
                  width: "50px",
                  height: "50px",
                  textAlign: "center",
                  fontSize: "20px",
                  border: "1px solid #ccc",
                  borderRadius: "5px",
                }}
              />
            ))}
          </div>

          <button
            className="btn btn-success w-100"
            onClick={handleVerifyOTP}
            disabled={verifying}
          >
            {verifying ? "Verifying..." : "Verify & Continue"}
          </button>
        </>
      )}
    </div>
  );
};

export default OTPVerificationPage;
